// src/pages/SchoolMetrics.js
import React, { useEffect, useState } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import "chart.js/auto";
import { useNavigate } from "react-router-dom";
import { getDocs, collection, query, where } from "firebase/firestore";
import { db } from "../firebase";
import "../App.css";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useMediaQuery } from "@mui/material";

Chart.register(ChartDataLabels);

const SchoolMetrics = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("All");
  const [weeklyLabels, setWeeklyLabels] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // --- helpers for dates coming from Firestore / plain JS ---
  const toMs = (v) => {
    if (!v) return null;
    if (typeof v === "number") return v > 1e12 ? v : v * 1000; // seconds → ms
    if (v?.toMillis) {
      try {
        return v.toMillis();
      } catch {}
    }
    if (v?._seconds != null) return v._seconds * 1000;
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : null;
  };
  const weekStart = (d) => {
    const s = new Date(d);
    s.setDate(d.getDate() - d.getDay());
    s.setHours(0, 0, 0, 0);
    return s;
  };

  useEffect(() => {
    // fetch once
    const fetchStudentData = async () => {
      try {
        const schoolId = localStorage.getItem("schoolId");
        if (!schoolId) return;

        const q = query(collection(db, "students"), where("schoolId", "==", schoolId));
        const snapshot = await getDocs(q);

        const allStudents = [];
        snapshot.forEach((d) => {
          const student = { id: d.id, ...d.data() };
if (student.firstName) {
  if (!student.className) student.className = "Unassigned";
  allStudents.push(student);
}

        });

        setStudents(allStudents);
      } catch (error) {
        console.error("Error fetching student data:", error);
      }
    };

    fetchStudentData();
  }, []);

  useEffect(() => {
  const calculateWeeklyData = (studentList) => {
    const filtered = studentList.filter(
      (s) => selectedClass === "All" || s.className === selectedClass
    );

    const weeklyMap = new Map();

    filtered.forEach((student) => {
      const activity = student.readingActivity || student.activityLog || [];
      activity.forEach((entry) => {
        const ms = toMs(entry?.date);
        if (ms == null) return;
        const d = new Date(ms);
        const start = weekStart(d).getTime();
        if (!weeklyMap.has(start)) weeklyMap.set(start, new Set());
        weeklyMap
          .get(start)
          .add(student.id || student.uid || student.username || student.firstName);
      });
    });

    let entries = Array.from(weeklyMap.entries()).sort((a, b) => a[0] - b[0]);

    const MAX_WEEKS = 12;
    if (entries.length > MAX_WEEKS) {
      entries = entries.slice(entries.length - MAX_WEEKS);
    }

    const labels = entries.map(([start]) => {
      const s = new Date(start);
      return `${s.getDate()} ${s.toLocaleString("default", { month: "short" })}`;
    });

    const data = entries.map(([, set]) => set.size);

    setWeeklyLabels(labels);
    setWeeklyData(data);
  };

  calculateWeeklyData(students);
}, [students, selectedClass]);

  const filteredStudents = students.filter(
    (s) => selectedClass === "All" || s.className === selectedClass
  );

  const genreCounts = {};
  const classStories = {};

  filteredStudents.forEach((s) => {
    if (s.genresExplored) {
  if (Array.isArray(s.genresExplored)) {
    s.genresExplored.forEach((g) => {
      if (!g) return;
      genreCounts[g] = (genreCounts[g] || 0) + 1;
    });
  } else {
    Object.entries(s.genresExplored).forEach(([genre, count]) => {
      genreCounts[genre] = (genreCounts[genre] || 0) + (Number(count) || 0);
    });
  }
}


    classStories[s.className] = (classStories[s.className] || 0) + (s.storiesRead || 0);
  });

  const usageTrends = {
    labels: weeklyLabels,
    datasets: [
      {
        label: "Weekly Active Users",
        data: weeklyData,
        borderColor: "rgba(0, 123, 255, 1)",
        borderWidth: 2,
        fill: false,
      },
    ],
  };

  const contentPreferences = {
    labels: Object.keys(genreCounts),
    datasets: [
      {
        label: "Content Preferences",
        data: Object.values(genreCounts),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
        ],
      },
    ],
  };

  const performanceComparisons = {
    labels: Object.keys(classStories),
    datasets: [
      {
        label: "Stories Read",
        data: Object.values(classStories),
        backgroundColor: "rgba(0, 123, 255, 0.6)",
      },
    ],
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById("charts-container");
    if (!element) return;

    html2canvas(element, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      // Build dynamic title and filename
      const titleText =
        selectedClass === "All"
          ? "All Classes - School Metrics"
          : `${selectedClass} - School Metrics`;
      const fileName = `${
        selectedClass === "All" ? "All_Classes" : selectedClass.replace(/\s+/g, "_")
      }_Metrics_${new Date().toISOString().slice(0, 10)}.pdf`;

      // Centered and underlined title
      const titleFontSize = 14;
      pdf.setFontSize(titleFontSize);
      pdf.setFont("helvetica", "bold");

      const textWidth = pdf.getTextWidth(titleText);
      const xPosition = (pageWidth - textWidth) / 2;
      const yPosition = 15;

      pdf.text(titleText, xPosition, yPosition);

      // Underline the title
      pdf.setDrawColor(0);
      pdf.setLineWidth(0.5);
      pdf.line(xPosition, yPosition + 1, xPosition + textWidth, yPosition + 1);

      // Adjust chart image position
      const y = yPosition + 10;
      if (imgHeight > pageHeight - y) {
        pdf.addImage(imgData, "PNG", 0, y, imgWidth, pageHeight - y - 10);
      } else {
        pdf.addImage(imgData, "PNG", 0, y, imgWidth, imgHeight);
      }

      pdf.save(fileName);
    });
  };


  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/school-dashboard")} className="header-button">
            Back to Dashboard
          </button>
        </nav>
      </header>

      <div style={{ padding: "20px", overflowX: "auto" }}>
        {/* ✅ allow horizontal scroll if needed */}
        <h1 style={{ color: "#007BFF", textAlign: "center", fontFamily: "Montserrat, sans-serif" }}>
  School Metrics
</h1>
<div
  style={{
    maxWidth: "900px",
    margin: "10px auto 0",
    background: "rgba(0,0,0,0.55)",
    padding: "12px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#ffffff",
    textAlign: "center",
  }}
>
  This dashboard provides a high-level view of reading engagement across your school.
  Use the class filter to explore patterns, compare engagement, and identify areas needing support.
</div>


        {/* Filter + Export PDF */}
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            marginTop: "10px",
            background: "rgba(255,255,255,0.9)",
            padding: "10px 20px",
            borderRadius: "8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <label>
              <strong>Filter by Class:&nbsp;</strong>
            </label>
            <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
              <option value="All">All Classes</option>
              {[...new Set(students.map((s) => s.className))].map((cls, i) => (
                <option key={i} value={cls}>
                  {cls}
                </option>
              ))}
            </select>
          </div>
          <button
            style={{
              padding: "6px 12px",
              backgroundColor: "#007BFF",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={handleDownloadPDF}
          >
            Download PDF of Charts
          </button>
          <p
  style={{
    marginTop: "6px",
    fontSize: "12px",
    color: "#555",
  }}
>
  Exports the currently selected charts and filters as a PDF.
  Ideal for sharing with SLT, governors, or inspection evidence.
</p>


        </div>

        {/* All Charts */}
        <div
          id="charts-container"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", // ✅ stack on small screens
            gap: "20px",
            marginTop: "20px",
            maxWidth: "1200px",
            marginInline: "auto",
            minWidth: 0, // ✅ prevent children from forcing overflow
          }}
        >
          {/* Weekly Active Users */}
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "10px",
              height: "400px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <h2>Weekly Active Users</h2>
            <p
  style={{
    marginTop: "-4px",
    marginBottom: "8px",
    fontSize: "13px",
    color: "#555",
  }}
>
  Shows how many unique students read at least one story each week.
  Displayed for the most recent 12 weeks to keep trends clear.
</p>


            <div style={{ height: "300px" }}>
              <Line
                data={usageTrends}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
  ticks: { autoSkip: true, maxTicksLimit: isMobile ? 6 : 12 },
  title: {
    display: true,
    text: "Week",
    color: "#000",
    font: { size: 14, weight: "bold" },
  },
},

                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: "Active Students",
                        color: "#000",
                        font: { size: 14, weight: "bold" },
                      },
                      ticks: {
                        stepSize: 1,
                        precision: 0,
                        callback: (value) => (Number.isInteger(value) ? value : null),
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Content Preferences */}
      
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "10px",
              height: "400px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <h2>Content Preferences</h2>
           <p
  style={{
    marginTop: "-4px",
    marginBottom: "8px",
    fontSize: "13px",
    color: "#555",
    textAlign: "center",
  }}
>
  Based on the genres students choose when generating and reading stories.
  Percentages show overall reading preferences for the selected class(es).
</p>



            <div
              style={{
                width: "100%",
                maxWidth: "350px",
                margin: "0 auto",
                height: "300px",
              }}
            >
              <Pie
                data={contentPreferences}
                options={{
                  plugins: {
                    datalabels: {
                      formatter: (value, context) => {
                        const arr = context.chart.data.datasets[0].data;
                        const total = arr.reduce((a, b) => a + b, 0);
                        if (!total) return "";
                        const percent = ((value / total) * 100).toFixed(1);
                        return `${percent}%`;
                      },
                      color: "#fff",
                      font: { weight: "bold" },
                      anchor: "center",
                      align: "center",
                      clamp: true,
                    },
                    legend: {
                      display: true,
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Performance Comparisons */}
          <div
            style={{
              gridColumn: isMobile ? "auto" : "1 / span 2", // ✅ no forced 2-column span on phones
              background: "#fff",
              padding: "20px",
              borderRadius: "10px",
              height: "400px",
            }}
          >
            <h2>Performance Comparisons</h2>
            <p
  style={{
    marginTop: "-4px",
    marginBottom: "10px",
    fontSize: "13px",
    color: "#555",
  }}
>
  Compares total stories read across classes to highlight engagement patterns
  and identify where additional support may be helpful.
</p>


            <div style={{ height: "300px" }}>
              <Bar
                data={performanceComparisons}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    datalabels: { display: false },
                    legend: { display: true },
                  },
                  scales: {
                    x: {
                      title: { display: true, text: "Class" },
                    },
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Stories Read" },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

        <footer className="page-footer" style={{ marginTop: "40px" }}>
          <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
          <nav style={{ marginTop: "10px" }}>
            <a href="/about" className="footer-link">
              About Us
            </a>
            <a href="/contact" className="footer-link">
              Contact
            </a>
          </nav>
        </footer>
      </div>
    </div>
  );
};

export default SchoolMetrics;
